import { CheckCircle2, Eye, Loader2, Scale, XCircle } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import type { TestRules } from "@/lib/testRules";

/** FEAT-16 — "Test rules": what we ask of the candidate, what is prohibited,
 *  what else is monitored and that it is recorded. Shown once, after Welcome
 *  and before the browser-setup step and the form, so it is read before
 *  anything is agreed to and before the clock.
 *
 *  The lists come from `buildTestRules` (lib/testRules.ts), which reads the
 *  test's own switches — this component only lays them out. `rules` is null
 *  while `/preview` is still loading: the step then shows nothing it could get
 *  wrong and Continue waits.
 *
 *  An acknowledgement, not a consent: the button's own label is the
 *  acknowledgement, nothing is ticked and nothing is recorded. The consent
 *  ticks stay on the form, untouched (legal copy). The one link opens the
 *  privacy notice in a new tab — fine here, the test has not started.
 *
 *  The two lists, their titles and the closing line are Emilia's words (Sheet
 *  #151, 2026-10-06), kept short on purpose. Still placeholder (owed.md): the
 *  "Before you start" eyebrow, the monitoring block's title and lines, the
 *  privacy-notice sentence and the button. */
export function TestRulesStep({
  rules,
  onContinue,
}: {
  rules: TestRules | null;
  onContinue: () => void;
}) {
  return (
    <div className="flex min-h-full items-center justify-center bg-background p-6">
      <Card className="w-full max-w-lg" data-testid="test-rules">
        <CardHeader className="space-y-2">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">
            Before you start
          </p>
          <CardTitle as="h1" className="text-2xl">
            Test rules
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {rules === null ? (
            <p
              className="flex items-center gap-2 text-sm text-muted-foreground"
              role="status"
            >
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              Loading the rules for this test…
            </p>
          ) : (
            <>
              <RuleList
                testId="rules-asked"
                title="We ask you to:"
                icon={CheckCircle2}
                iconClass="text-success"
                items={rules.asked}
              />
              <RuleList
                testId="rules-prohibited"
                title="During the test, the following is prohibited:"
                icon={XCircle}
                iconClass="text-destructive"
                items={rules.prohibited}
              />
              {rules.monitoring.length > 0 && (
                <RuleList
                  testId="rules-monitoring"
                  title="Monitoring runs in the background"
                  icon={Eye}
                  iconClass="text-muted-foreground"
                  items={rules.monitoring}
                />
              )}
              {/* Her closing line stands alone, as it does in her text — no
                  heading over it. */}
              <p
                className="flex items-start gap-2 rounded-md border border-info/40 bg-info/5 p-4 text-sm text-foreground/90"
                data-testid="rules-closing"
              >
                <Scale
                  className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground"
                  aria-hidden="true"
                />
                <span>{rules.closing}</span>
              </p>
              <p className="text-xs text-muted-foreground">
                What is stored about you, and for how long, is in the{" "}
                <a
                  href="/privacy-notice"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-foreground"
                >
                  privacy notice
                </a>
                .
              </p>
            </>
          )}
        </CardContent>
        <CardFooter>
          <Button
            type="button"
            className="w-full cursor-pointer min-h-11"
            disabled={rules === null}
            onClick={onContinue}
          >
            I understand the rules — continue
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}

function RuleList({
  testId,
  title,
  icon: Icon,
  iconClass,
  items,
}: {
  testId: string;
  title: string;
  icon: typeof CheckCircle2;
  iconClass: string;
  items: string[];
}) {
  const titleId = `${testId}-title`;
  return (
    <section
      className="space-y-2 rounded-md border border-border bg-muted/30 p-4"
      aria-labelledby={titleId}
      data-testid={testId}
    >
      <h2
        id={titleId}
        className="text-xs font-medium uppercase tracking-wide text-muted-foreground"
      >
        {title}
      </h2>
      <ul className="space-y-2 text-sm">
        {items.map((item) => (
          <li key={item} className="flex items-start gap-2">
            <Icon
              className={`mt-0.5 h-4 w-4 shrink-0 ${iconClass}`}
              aria-hidden="true"
            />
            <span className="text-foreground/90">{item}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}
